import { useEffect, useState } from 'react'
import { Check, Copy, UserPlus, Zap } from 'lucide-react'
import { billingApi, type BillingStatus, type Boost, type Referrals, type Showcase } from '@/lib/billingApi'
import { describeError } from '@/lib/auth'
import { guildIconUrl } from '@/lib/avatar'
import { guildColor, guildInitials, plural } from '@/lib/guildUi'
import { useGuildStore } from '@/store/guilds'
import { cx } from '@/lib/cx'
import { Avatar } from '../Avatar'
import { ContextMenu, MenuItem } from '../ContextMenu'
import { PlusMark } from '../PlusMark'
import layer from './SettingsLayer.module.css'
import styles from './PremiumSection.module.css'

const inviteLink = (code: string) => `${window.location.origin}/r/${code}`
const money = (rub: number) => `${rub.toLocaleString('ru-RU')} ₽`
const date = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })
const boostsLabel = (n: number) => `${n} ${plural(n, 'буст', 'буста', 'бустов')}`
/** Ближайший конец: у бустов одного сервера сроки могут разниться, раньше кончится этот. */
const soonest = (list: Boost[]) => list.reduce((m, b) => (b.ends_at < m ? b.ends_at : m), list[0]?.ends_at ?? '')

/**
 * «Кличат Плюс»: что даёт, как получить за приглашённых, свои бусты. Тарифы, автопродление и
 * платежи показываются, только когда включена оплата (showcase.enabled): пока сервис бесплатный,
 * Плюс и бусты раздаются за приглашённых, и цены с кнопками «купить» только путали бы.
 */
export function PremiumSection() {
  const [showcase, setShowcase] = useState<Showcase | null>(null)
  const [status, setStatus] = useState<BillingStatus | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [refs, setRefs] = useState<Referrals | null>(null)
  const [copied, setCopied] = useState(false)
  const [placeMenu, setPlaceMenu] = useState<{ x: number; y: number } | null>(null)
  const guilds = useGuildStore((s) => s.guilds)

  const load = () => {
    void billingApi.showcase().then(setShowcase).catch(() => undefined)
    void billingApi.status().then(setStatus).catch((err: unknown) => setError(describeError(err).message))
    void billingApi.referrals().then(setRefs).catch(() => undefined)
  }
  useEffect(load, [])

  const buy = (plan: string) => {
    setBusy(true)
    setError(null)
    void billingApi
      .checkout(plan)
      .then(({ confirmation_url }) => {
        window.location.href = confirmation_url
      })
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  const release = (guildId: string, boostId: string) => {
    setBusy(true)
    void billingApi
      .unboost(guildId, boostId)
      .then(load)
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  // свободный буст — сразу на сервер отсюда, а не «поставьте его на вкладке «Буст»»
  const place = (guildId: string) => {
    setPlaceMenu(null)
    setBusy(true)
    setError(null)
    void billingApi
      .boost(guildId)
      .then(load)
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  // бусты по серверам: строка на сервер; свободные — одной строкой
  const placed = new Map<string, Boost[]>()
  const free: Boost[] = []
  for (const b of status?.boosts ?? []) {
    if (b.guild_id) placed.set(b.guild_id, [...(placed.get(b.guild_id) ?? []), b])
    else free.push(b)
  }
  const myGuilds = Object.values(guilds).sort((a, b) => a.name.localeCompare(b.name, 'ru'))

  const plus = showcase?.plus
  const paid = Boolean(showcase?.enabled)
  const plusPlans = showcase?.plans.filter((p) => p.Kind === 'plus') ?? []
  const boostPlan = showcase?.plans.find((p) => p.Kind === 'boost')

  return (
    <div>
      <h1 className={cx(layer.sectionTitle, styles.title)}><PlusMark size={40} label={null} /> Кличат Плюс</h1>
      {status?.plus ? (
        <p className={layer.text}>
          {/* дата кончается на «г.» — вторая точка после неё не нужна */}
          Плюс действует{status.plus_until ? ` до ${date(status.plus_until)}` : '.'}{paid ? (status.auto_renew ? ' Продлевается автоматически.' : ' Автопродление выключено.') : ''}
        </p>
      ) : (
        <p className={layer.text}>Больше файлов, длиннее сообщения, стикеры любого сервера в любом чате и бусты для своих серверов.</p>
      )}
      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      {plus ? (
        <ul className={styles.perks}>
          <li><Check size={16} /> Вложения до {plus.upload_mb} МБ вместо {showcase?.free.upload_mb} МБ</li>
          <li><Check size={16} /> Сообщения до {plus.message_len.toLocaleString('ru-RU')} символов</li>
          <li><Check size={16} /> Демонстрация экрана {plus.stream_height}p при {plus.stream_fps} кадрах</li>
          <li><Check size={16} /> Стикеры любого своего сервера — в любом чате</li>
          <li><Check size={16} /> Анимированные аватар и баннер</li>
        </ul>
      ) : null}

      <h2 className={layer.subTitle}>Плюс за приглашённых</h2>
      {refs ? (
        <>
          <p className={layer.text}>Приведите {refs.need === 1 ? 'одного человека' : `${refs.need} человек`} — и получите месяц Плюса, а с ним два буста.</p>
          <ul className={layer.points}>
            <li>приглашённый засчитывается, когда напишет сообщение или проведёт минуту в голосовом канале</li>
            <li>каждый — один раз; новые приглашения продлевают Плюс</li>
          </ul>
          <div className={styles.invite}>
            <input className={styles.inviteLink} readOnly value={inviteLink(refs.code)} onFocus={(e) => e.currentTarget.select()} aria-label="Ссылка-приглашение" />
            <button
              type="button"
              className={layer.button}
              onClick={() => {
                void navigator.clipboard.writeText(inviteLink(refs.code)).then(() => {
                  setCopied(true)
                  setTimeout(() => setCopied(false), 2000)
                })
              }}
            >
              {copied ? <><Check size={16} /> Скопировано</> : <><Copy size={16} /> Скопировать</>}
            </button>
          </div>
          <div className={styles.progress}>
            <div className={styles.progressBar}>
              <span className={styles.progressFill} style={{ width: `${Math.min(100, Math.round((refs.pending / refs.need) * 100))}%` }} />
            </div>
            <span className={styles.progressText}>
              <UserPlus size={14} /> {refs.pending} из {refs.need} до следующего месяца
            </span>
          </div>
          <p className={layer.text}>Всего привёл: {refs.invited}, из них дошли до первого действия: {refs.qualified}.</p>
        </>
      ) : null}

      {paid ? (
        <>
          <h2 className={layer.subTitle}>Подписка</h2>
          <div className={styles.cards}>
            {plusPlans.map((p) => (
              <div key={p.ID} className={styles.card}>
                <span className={styles.cardName}><PlusMark size={20} label={null} /> {p.Name}</span>
                <span className={styles.price}>{money(p.PriceRub)}</span>
                <span className={styles.hint}>{p.Months === 12 ? 'за год — два месяца в подарок' : 'за месяц'}</span>
                <button type="button" className={layer.button} disabled={busy || !showcase?.enabled} onClick={() => buy(p.ID)}>
                  {status?.plus ? 'Продлить' : 'Оформить'}
                </button>
              </div>
            ))}
            {boostPlan ? (
              <div className={styles.card}>
                <span className={styles.cardName}><Zap size={16} /> {boostPlan.Name}</span>
                <span className={styles.price}>{money(boostPlan.PriceRub)}</span>
                <span className={styles.hint}>слот, который можно поставить любому серверу</span>
                <button type="button" className={layer.button} disabled={busy || !showcase?.enabled} onClick={() => buy(boostPlan.ID)}>Купить буст</button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}

      <h2 className={layer.subTitle}>Мои бусты</h2>
      {status && status.boosts.length > 0 ? (
        <ul className={styles.myBoosts}>
          {[...placed].map(([guildId, list]) => {
            const g = guilds[guildId]
            const name = g?.name ?? 'Сервер'
            return (
              <li key={guildId} className={styles.myBoost}>
                <Avatar initial={guildInitials(name)} color={guildColor(guildId)} size={40} src={g ? guildIconUrl(g.id, g.icon) : undefined} />
                <span className={styles.myBoostText}>
                  <span className={styles.myBoostName}>{name}</span>
                  <span className={styles.myBoostSub}>
                    <span className={styles.boostCount}><Zap size={12} aria-hidden="true" /> {boostsLabel(list.length)}</span> · до {date(soonest(list))}
                  </span>
                </span>
                <button
                  type="button"
                  className={cx(layer.button, layer.buttonSecondary, styles.myBoostAction)}
                  disabled={busy}
                  data-tooltip={list.length > 1 ? 'Снять один буст с этого сервера' : 'Снять буст с этого сервера'}
                  onClick={() => release(guildId, list[0]!.id)}
                >
                  {list.length > 1 ? 'Снять один' : 'Снять'}
                </button>
              </li>
            )
          })}
          {free.length ? (
            <li className={styles.myBoost}>
              <span className={styles.freeIcon} aria-hidden="true"><Zap size={20} /></span>
              <span className={styles.myBoostText}>
                <span className={styles.myBoostName}>{free.length > 1 ? 'Свободные бусты' : 'Свободный буст'}</span>
                <span className={styles.myBoostSub}>
                  <span className={styles.boostCount}><Zap size={12} aria-hidden="true" /> {boostsLabel(free.length)}</span> · до {date(soonest(free))}
                </span>
              </span>
              <button
                type="button"
                className={cx(layer.button, styles.myBoostAction)}
                disabled={busy || myGuilds.length === 0}
                aria-haspopup="menu"
                aria-expanded={Boolean(placeMenu)}
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect()
                  setPlaceMenu(placeMenu ? null : { x: r.right, y: r.bottom + 4 })
                }}
              >
                Поставить на сервер
              </button>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className={layer.text}>Бустов пока нет. Два буста приходят с месяцем Плюса за приглашённых.</p>
      )}

      {placeMenu ? (
        <ContextMenu x={placeMenu.x} y={placeMenu.y} align="right" label="На какой сервер" onClose={() => setPlaceMenu(null)}>
          {myGuilds.map((g) => (
            <MenuItem
              key={g.id}
              icon={<Avatar initial={guildInitials(g.name)} color={guildColor(g.id)} size={20} src={guildIconUrl(g.id, g.icon)} />}
              label={g.name}
              onClick={() => place(g.id)}
            />
          ))}
        </ContextMenu>
      ) : null}

      {paid && status && status.payments.length > 0 ? (
        <>
          <h2 className={layer.subTitle}>Платежи</h2>
          <ul className={styles.boosts}>
            {status.payments.map((p) => (
              <li key={p.id}>
                <span>{date(p.created_at)} · {p.plan} · {money(p.amount_rub)}</span>
                <span className={styles.hint}>{p.status === 'succeeded' ? 'оплачен' : p.status === 'pending' ? 'ожидает оплаты' : 'отменён'}</span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}
